import React, { useState, useEffect } from 'react';
import Header from '../components/layout/Header';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  User, 
  Mail, 
  Phone, 
  Briefcase, 
  Scale,
  Award,
  TrendingUp,
  FileText,
  Users
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LawyersPage = () => {
  const { language } = useLanguage();
  const { token } = useAuth();
  const [lawyers, setLawyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLawyer, setSelectedLawyer] = useState(null);
  const [lawyerStats, setLawyerStats] = useState({});

  const api = axios.create({
    baseURL: `${API_URL}/api`,
    headers: { Authorization: `Bearer ${token}` }
  });

  useEffect(() => {
    fetchLawyers();
  }, []);

  const fetchLawyers = async () => {
    try {
      const response = await api.get('/users');
      const lawyersList = response.data.filter(u => u.role === 'lawyer');
      setLawyers(lawyersList);
      
      // Fetch stats for each lawyer
      const statsPromises = lawyersList.map(async (lawyer) => {
        try {
          const [casesRes, perfRes] = await Promise.all([
            api.get('/cases'),
            api.get('/finance/employee-performance').catch(() => ({ data: [] }))
          ]);
          
          const assignedCases = casesRes.data.filter(c => c.assigned_lawyer_id === lawyer.id);
          const perfData = perfRes.data.find(p => p.lawyer_id === lawyer.id);
          
          return {
            id: lawyer.id,
            totalCases: assignedCases.length,
            activeCases: assignedCases.filter(c => c.status === 'active').length,
            closedCases: assignedCases.filter(c => c.status === 'closed').length,
            revenue: perfData?.total_revenue || 0
          };
        } catch {
          return { id: lawyer.id, totalCases: 0, activeCases: 0, closedCases: 0, revenue: 0 };
        }
      });
      
      const stats = await Promise.all(statsPromises);
      const statsMap = {};
      stats.forEach(s => { statsMap[s.id] = s; });
      setLawyerStats(statsMap);
      
    } catch (error) {
      console.error('Error fetching lawyers:', error);
      toast.error(language === 'ar' ? 'حدث خطأ' : 'Error occurred');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(language === 'ar' ? 'ar-IQ' : 'en-US', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount) + ' د.ع.';
  };

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').slice(0, 2);
  };

  const lawyerColors = [
    'bg-blue-500',
    'bg-emerald-500',
    'bg-purple-500',
    'bg-amber-500',
    'bg-rose-500',
    'bg-cyan-500'
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="lawyers-page">
      <Header title={language === 'ar' ? 'فريق المحامين' : 'Legal Team'} />
      
      <div className="p-6">
        {/* Team Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="stat-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">
                    {language === 'ar' ? 'عدد المحامين' : 'Total Lawyers'}
                  </p>
                  <h3 className="text-2xl font-bold mt-1">{lawyers.length}</h3>
                </div>
                <div className="p-3 rounded-sm bg-[hsl(var(--accent))]/10">
                  <Users className="w-6 h-6 text-[hsl(var(--accent))]" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="stat-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">
                    {language === 'ar' ? 'إجمالي القضايا' : 'Total Cases'}
                  </p>
                  <h3 className="text-2xl font-bold mt-1">
                    {Object.values(lawyerStats).reduce((sum, s) => sum + s.totalCases, 0)}
                  </h3>
                </div>
                <div className="p-3 rounded-sm bg-blue-500/10">
                  <Briefcase className="w-6 h-6 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="stat-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">
                    {language === 'ar' ? 'القضايا النشطة' : 'Active Cases'}
                  </p>
                  <h3 className="text-2xl font-bold mt-1 text-emerald-600">
                    {Object.values(lawyerStats).reduce((sum, s) => sum + s.activeCases, 0)}
                  </h3>
                </div>
                <div className="p-3 rounded-sm bg-emerald-500/10">
                  <Scale className="w-6 h-6 text-emerald-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="stat-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">
                    {language === 'ar' ? 'إجمالي الإيرادات' : 'Total Revenue'}
                  </p>
                  <h3 className="text-2xl font-bold mt-1 text-[hsl(var(--accent))]">
                    {formatCurrency(Object.values(lawyerStats).reduce((sum, s) => sum + s.revenue, 0))}
                  </h3>
                </div>
                <div className="p-3 rounded-sm bg-[hsl(var(--accent))]/10">
                  <TrendingUp className="w-6 h-6 text-[hsl(var(--accent))]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Lawyers Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {lawyers.map((lawyer, index) => {
            const stats = lawyerStats[lawyer.id] || { totalCases: 0, activeCases: 0, closedCases: 0, revenue: 0 };
            const colorClass = lawyerColors[index % lawyerColors.length];
            
            return (
              <Card 
                key={lawyer.id} 
                className="hover:shadow-lg transition-shadow cursor-pointer group"
                onClick={() => setSelectedLawyer(lawyer)}
                data-testid={`lawyer-card-${lawyer.id}`}
              >
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    {/* Avatar */}
                    <div className={`w-16 h-16 rounded-full ${colorClass} flex items-center justify-center text-white text-xl font-bold shadow-lg group-hover:scale-105 transition-transform`}>
                      {getInitials(lawyer.full_name)}
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-lg truncate">{lawyer.full_name}</h3>
                      <Badge variant="outline" className="mt-1">
                        <Scale className="w-3 h-3 mr-1" />
                        {language === 'ar' ? 'محامي' : 'Lawyer'}
                      </Badge>
                      
                      <div className="mt-3 space-y-1 text-sm text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          <span className="truncate">{lawyer.email}</span>
                        </div>
                        {lawyer.phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4" />
                            <span>{lawyer.phone}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Stats */}
                  <div className="mt-4 pt-4 border-t border-border grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-2xl font-bold text-[hsl(var(--accent))]">{stats.totalCases}</p>
                      <p className="text-xs text-muted-foreground">
                        {language === 'ar' ? 'قضايا' : 'Cases'}
                      </p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-emerald-600">{stats.activeCases}</p>
                      <p className="text-xs text-muted-foreground">
                        {language === 'ar' ? 'نشطة' : 'Active'}
                      </p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-amber-600">{stats.closedCases}</p>
                      <p className="text-xs text-muted-foreground">
                        {language === 'ar' ? 'مغلقة' : 'Closed'}
                      </p>
                    </div>
                  </div>
                  
                  {/* Revenue */}
                  {stats.revenue > 0 && (
                    <div className="mt-3 p-2 rounded-sm bg-emerald-50 dark:bg-emerald-900/20 text-center">
                      <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                        {language === 'ar' ? 'الإيرادات:' : 'Revenue:'} {formatCurrency(stats.revenue)}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {lawyers.length === 0 && (
          <Card className="p-12 text-center">
            <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" strokeWidth={1} />
            <p className="text-muted-foreground">
              {language === 'ar' ? 'لا يوجد محامين' : 'No lawyers found'}
            </p>
          </Card>
        )}

        {/* Lawyer Detail Dialog */}
        <Dialog open={!!selectedLawyer} onOpenChange={() => setSelectedLawyer(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {language === 'ar' ? 'ملف المحامي' : 'Lawyer Profile'}
              </DialogTitle>
            </DialogHeader>
            
            {selectedLawyer && (
              <div className="space-y-6">
                {/* Profile Header */}
                <div className="flex items-center gap-4">
                  <div className={`w-20 h-20 rounded-full ${lawyerColors[lawyers.findIndex(l => l.id === selectedLawyer.id) % lawyerColors.length]} flex items-center justify-center text-white text-2xl font-bold shadow-lg`}>
                    {getInitials(selectedLawyer.full_name)}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">{selectedLawyer.full_name}</h2>
                    <Badge className="mt-1 bg-[hsl(var(--accent))]">
                      <Scale className="w-3 h-3 mr-1" />
                      {language === 'ar' ? 'محامي' : 'Lawyer'}
                    </Badge>
                  </div>
                </div>
                
                {/* Contact Info */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm text-muted-foreground uppercase">
                    {language === 'ar' ? 'معلومات الاتصال' : 'Contact Information'}
                  </h3>
                  <div className="space-y-2">
                    <div className="flex items-center gap-3 p-2 rounded-sm bg-muted/50">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      <span>{selectedLawyer.email}</span>
                    </div>
                    {selectedLawyer.phone && (
                      <div className="flex items-center gap-3 p-2 rounded-sm bg-muted/50">
                        <Phone className="w-4 h-4 text-muted-foreground" />
                        <span>{selectedLawyer.phone}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-3 p-2 rounded-sm bg-muted/50">
                      <User className="w-4 h-4 text-muted-foreground" />
                      <span>@{selectedLawyer.username}</span>
                    </div>
                  </div>
                </div>
                
                {/* Performance Stats */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm text-muted-foreground uppercase">
                    {language === 'ar' ? 'إحصائيات الأداء' : 'Performance Stats'}
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-sm bg-blue-50 dark:bg-blue-900/20 text-center">
                      <Briefcase className="w-5 h-5 mx-auto text-blue-600 mb-1" />
                      <p className="text-xl font-bold text-blue-600">
                        {lawyerStats[selectedLawyer.id]?.totalCases || 0}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {language === 'ar' ? 'إجمالي القضايا' : 'Total Cases'}
                      </p>
                    </div>
                    <div className="p-3 rounded-sm bg-emerald-50 dark:bg-emerald-900/20 text-center">
                      <Scale className="w-5 h-5 mx-auto text-emerald-600 mb-1" />
                      <p className="text-xl font-bold text-emerald-600">
                        {lawyerStats[selectedLawyer.id]?.activeCases || 0}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {language === 'ar' ? 'قضايا نشطة' : 'Active Cases'}
                      </p>
                    </div>
                    <div className="p-3 rounded-sm bg-amber-50 dark:bg-amber-900/20 text-center">
                      <Award className="w-5 h-5 mx-auto text-amber-600 mb-1" />
                      <p className="text-xl font-bold text-amber-600">
                        {lawyerStats[selectedLawyer.id]?.closedCases || 0}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {language === 'ar' ? 'قضايا مكتملة' : 'Completed'}
                      </p>
                    </div>
                    <div className="p-3 rounded-sm bg-purple-50 dark:bg-purple-900/20 text-center">
                      <TrendingUp className="w-5 h-5 mx-auto text-purple-600 mb-1" />
                      <p className="text-lg font-bold text-purple-600">
                        {formatCurrency(lawyerStats[selectedLawyer.id]?.revenue || 0)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {language === 'ar' ? 'الإيرادات' : 'Revenue'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default LawyersPage;
